import { useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useProfile, OrganizationProfile } from "@/context/ProfileContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  RECIPIENT_TYPE_LABELS,
  RECIPIENT_TYPE_DESCRIPTIONS,
  validateIBAN,
  validateEmail,
  validatePhone,
  validateEDRPOU,
  validateLegalAddress,
} from "@/lib/application";
import { cn } from "@/lib/utils";

function getAuthTypeLabel(authType?: string): string {
  const labels: Record<string, string> = {
    dig_sign: "Електронний підпис (КЕП)",
    diia_id: "Дія.Підпис",
    bank_id: "BankID",
    diia_oauth: "Дія",
  };
  return labels[authType || ""] || "Невідомо";
}

function maskDrfocode(drfocode: string): string {
  if (drfocode.length <= 4) return drfocode;
  return drfocode.slice(0, 2) + "••••" + drfocode.slice(-4);
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function SaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

const RECIPIENT_ICONS = {
  osbb: BuildingIcon,
  manager: KeyIcon,
  cooperative: UsersIcon,
} as const;

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
  isIdGov?: boolean;
}

function Field({ label, required, error, children, hint, isIdGov }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-[13px] font-medium flex items-center gap-2">
        {label}
        {required && <span className="text-destructive">*</span>}
        {isIdGov && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
            <LockIcon className="w-3 h-3" />
            id.gov.ua
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

export function Settings() {
  const { user } = useAuth();
  const { profile, idGovFields, updateProfile, saveProfile, hasStoredProfile, isProfileComplete, isSaving, isLoading } = useProfile();
  const [ibanError, setIbanError] = useState<string | null>(null);
  const [ibanBankName, setIbanBankName] = useState<string | null>(null);
  const [isValidatingBank, setIsValidatingBank] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [edrpouError, setEdrpouError] = useState<string | null>(null);
  const [legalAddressError, setLegalAddressError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const initials = user?.givenname
    ? `${user.givenname.charAt(0)}${user.lastname?.charAt(0) || ""}`.toUpperCase()
    : "?";

  const handleTypeSelect = (type: OrganizationProfile["recipient_type"]) => {
    updateProfile({ recipient_type: type });
    setSaved(false);
  };

  const handleIbanChange = (value: string) => {
    const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 29);
    updateProfile({ iban: formatted });
    setSaved(false);
    setIbanBankName(null); // Clear bank name while typing

    if (formatted.length === 29) {
      const result = validateIBAN(formatted);
      setIbanError(result.valid ? null : result.error || null);
    } else {
      setIbanError(null);
    }
  };

  // Async bank name lookup on blur - always try to show bank name if found
  const handleIbanBlur = useCallback(async (value: string) => {
    if (!value || value.length === 0) return;
    
    const cleanValue = value.replace(/\s/g, "").toUpperCase();
    
    // Validate checksum (blocking validation)
    const basicResult = validateIBAN(cleanValue);
    setIbanError(basicResult.valid ? null : basicResult.error || null);
    
    // Always try to lookup bank name if we have enough characters for bank code
    // Bank code is at positions 4-10 (after "UA" + 2 control digits)
    if (cleanValue.length >= 10 && cleanValue.startsWith("UA")) {
      const bankCode = cleanValue.substring(4, 10);
      setIsValidatingBank(true);
      try {
        const { validateBankCode } = await import("@/lib/nbu");
        const bankResult = await validateBankCode(bankCode);
        setIbanBankName(bankResult.bankName || null);
      } catch (error) {
        console.warn("NBU bank lookup failed:", error);
        setIbanBankName(null);
      } finally {
        setIsValidatingBank(false);
      }
    } else {
      setIbanBankName(null);
    }
  }, []);

  const handlePhoneChange = (value: string) => {
    let digits = value.replace(/\D/g, "");
    if (!digits.startsWith("380")) {
      if (digits.startsWith("0")) {
        digits = "38" + digits;
      } else if (!digits.startsWith("38")) {
        digits = "380" + digits;
      }
    }
    const formatted = "+" + digits.slice(0, 12);
    updateProfile({ phone: formatted });
    setSaved(false);
    
    // Validate when complete
    if (formatted.length === 13) {
      const result = validatePhone(formatted);
      setPhoneError(result.valid ? null : result.error || null);
    } else {
      setPhoneError(null);
    }
  };

  const handlePhoneBlur = (value: string) => {
    // Validate on blur if not empty
    if (value && value.length > 4) { // More than just "+380"
      const result = validatePhone(value);
      setPhoneError(result.valid ? null : result.error || null);
    }
  };

  const handleEmailChange = (value: string) => {
    updateProfile({ email: value });
    setSaved(false);
    
    // Validate if has @ symbol (real-time validation while typing)
    if (value.includes("@")) {
      const result = validateEmail(value);
      setEmailError(result.valid ? null : result.error || null);
    } else {
      setEmailError(null);
    }
  };

  const handleEmailBlur = (value: string) => {
    // Validate on blur even without @
    if (value && value.length > 0) {
      const result = validateEmail(value);
      setEmailError(result.valid ? null : result.error || null);
    }
  };

  const handleEdrpouChange = (value: string) => {
    const formatted = value.replace(/\D/g, "").slice(0, 8);
    updateProfile({ edrpou_code: formatted });
    setSaved(false);
    
    // Validate when 8 digits
    if (formatted.length === 8) {
      const result = validateEDRPOU(formatted);
      setEdrpouError(result.valid ? null : result.error || null);
    } else {
      setEdrpouError(null);
    }
  };

  const handleEdrpouBlur = (value: string) => {
    // Validate on blur if not empty
    if (value && value.length > 0) {
      const result = validateEDRPOU(value);
      setEdrpouError(result.valid ? null : result.error || null);
    }
  };

  const handleLegalAddressChange = (value: string) => {
    updateProfile({ legal_address: value });
    setSaved(false);
    
    // Clear error when typing if now valid
    if (value.length >= 10) {
      setLegalAddressError(null);
    }
  };

  const handleLegalAddressBlur = (value: string) => {
    // Validate on blur if not empty
    if (value && value.length > 0) {
      const result = validateLegalAddress(value);
      setLegalAddressError(result.valid ? null : result.error || null);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await saveProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaveError("Не вдалося зберегти профіль. Спробуйте ще раз.");
      setTimeout(() => setSaveError(null), 5000);
    }
  };

  return (
    <AppLayout userRole="applicant">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Налаштування</h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            Профіль користувача та дані організації
          </p>
        </div>

        {/* User Profile from ID.gov.ua */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-[15px] font-semibold">Особисті дані</h2>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[11px] font-medium">
              id.gov.ua
            </span>
          </div>
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shrink-0">
              <span className="text-primary-foreground text-lg font-semibold">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] truncate" title={`${user?.lastname} ${user?.givenname} ${user?.middlename || ""}`.trim()}>
                {user?.lastname} {user?.givenname} {user?.middlename}
              </p>
              <p className="text-[13px] text-muted-foreground truncate" title={user?.email || "—"}>
                {user?.email || "—"}
              </p>
            </div>
          </div>

          {/* Dynamic ID.gov fields */}
          {(() => {
            // Basic fields - always show if have value
            const basicFields = [
              { key: "drfocode", label: "РНОКПП", value: user?.drfocode ? maskDrfocode(user.drfocode) : null, mono: true },
              { key: "edrpoucode", label: "ЄДРПОУ", value: user?.edrpoucode, mono: true },
              { key: "auth_type", label: "Авторизація", value: user?.auth_type ? getAuthTypeLabel(user.auth_type) : null },
              { key: "phone", label: "Телефон", value: user?.phone },
            ].filter(f => f.value);

            // КЕП certificate fields
            const kepFields = [
              { key: "organization_name", label: "Організація", value: user?.organization_name, fullWidth: true },
              { key: "position", label: "Посада", value: user?.position },
              { key: "locality", label: "Місто", value: user?.locality },
              { key: "region", label: "Область", value: user?.region },
              { key: "organization_unit", label: "Підрозділ", value: user?.organization_unit },
            ].filter(f => f.value);

            return (
              <>
                {basicFields.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-border/50 grid grid-cols-2 gap-4">
                    {basicFields.map((field) => (
                      <div key={field.key} className="min-w-0">
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{field.label}</p>
                        <p className={cn("text-[13px] mt-0.5 truncate", field.mono && "font-mono")} title={String(field.value)}>
                          {field.value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {kepFields.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-3">
                      Дані з сертифіката КЕП
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      {kepFields.map((field) => (
                        <div key={field.key} className={cn("min-w-0", field.fullWidth ? "col-span-2" : "")}>
                          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{field.label}</p>
                          <p className="text-[13px] mt-0.5 truncate" title={String(field.value)}>
                            {field.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          <div className="mt-4 p-3 bg-blue-50 rounded-xl">
            <p className="text-[11px] text-blue-700">
              Ці дані отримано з id.gov.ua і не можуть бути змінені в системі.
              Для зміни особистих даних зверніться до відповідних державних органів.
            </p>
          </div>
        </div>

        {/* Organization Profile */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-semibold">Профіль організації</h2>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Ці дані автоматично заповнюватимуться при створенні нової заявки
              </p>
            </div>
            {hasStoredProfile && (
              <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-lg text-[11px] font-medium">
                Збережено
              </span>
            )}
          </div>

          {/* Recipient type selection */}
          <div className="mb-6">
            <p className="text-[13px] font-medium mb-3">Форма управління</p>
            <div className="grid gap-3">
              {(["osbb", "manager", "cooperative"] as const).map((type) => {
                const Icon = RECIPIENT_ICONS[type];
                const isSelected = profile.recipient_type === type;

                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTypeSelect(type)}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-2xl border-[3px] text-left transition-all",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border/50 hover:border-foreground/20"
                    )}
                  >
                    <div
                      className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                        isSelected ? "bg-primary/10" : "bg-muted"
                      )}
                    >
                      <Icon className={cn("w-6 h-6", isSelected ? "text-primary" : "text-foreground/50")} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-[14px] font-semibold", isSelected && "text-primary")}>
                        {RECIPIENT_TYPE_LABELS[type]}
                      </p>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {RECIPIENT_TYPE_DESCRIPTIONS[type]}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <CheckIcon className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Organization details */}
          <div className="space-y-4">
            <Field
              label="Повна назва організації"
              required
              isIdGov={idGovFields.organization_name}
            >
              <input
                type="text"
                value={profile.organization_name}
                onChange={(e) => {
                  if (!idGovFields.organization_name) {
                    updateProfile({ organization_name: e.target.value });
                    setSaved(false);
                  }
                }}
                placeholder="Наприклад: ОСББ «Затишок»"
                disabled={idGovFields.organization_name}
                className={cn(
                  "w-full h-11 px-4 rounded-xl border-[3px] text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors",
                  idGovFields.organization_name
                    ? "border-blue-200 bg-blue-50 text-blue-900 cursor-not-allowed"
                    : "border-border/50 bg-white focus:border-primary/50"
                )}
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Код ЄДРПОУ"
                required
                hint={!edrpouError ? "8 цифр" : undefined}
                error={edrpouError || undefined}
                isIdGov={idGovFields.edrpou_code}
              >
                <input
                  type="text"
                  value={profile.edrpou_code}
                  onChange={(e) => {
                    if (!idGovFields.edrpou_code) {
                      handleEdrpouChange(e.target.value);
                    }
                  }}
                  onBlur={(e) => {
                    if (!idGovFields.edrpou_code) {
                      handleEdrpouBlur(e.target.value);
                    }
                  }}
                  placeholder="12345678"
                  disabled={idGovFields.edrpou_code}
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                    idGovFields.edrpou_code
                      ? "border-blue-200 bg-blue-50 text-blue-900 cursor-not-allowed"
                      : edrpouError
                        ? "border-destructive/50 focus:border-destructive"
                        : "border-border/50 bg-white focus:border-primary/50"
                  )}
                />
              </Field>

              <Field
                label="ПІБ керівника"
                required
                isIdGov={idGovFields.head_full_name}
              >
                <input
                  type="text"
                  value={profile.head_full_name}
                  onChange={(e) => {
                    if (!idGovFields.head_full_name) {
                      updateProfile({ head_full_name: e.target.value });
                      setSaved(false);
                    }
                  }}
                  placeholder="Іванов Іван Іванович"
                  disabled={idGovFields.head_full_name}
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors",
                    idGovFields.head_full_name
                      ? "border-blue-200 bg-blue-50 text-blue-900 cursor-not-allowed"
                      : "border-border/50 bg-white focus:border-primary/50"
                  )}
                />
              </Field>
            </div>

            <Field
              label="Юридична адреса"
              required
              hint={legalAddressError ? undefined : "Мінімум 10 символів"}
              error={legalAddressError || undefined}
            >
              <textarea
                value={profile.legal_address}
                onChange={(e) => handleLegalAddressChange(e.target.value)}
                onBlur={(e) => handleLegalAddressBlur(e.target.value)}
                placeholder="Повна юридична адреса організації"
                rows={2}
                className={cn(
                  "w-full px-4 py-3 rounded-xl border-[3px] bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors resize-none",
                  legalAddressError
                    ? "border-destructive/50 focus:border-destructive"
                    : "border-border/50 focus:border-primary/50"
                )}
              />
            </Field>

            <Field label="Фактична адреса">
              <textarea
                value={profile.actual_address}
                onChange={(e) => {
                  updateProfile({ actual_address: e.target.value });
                  setSaved(false);
                }}
                placeholder="Фактична адреса (якщо відрізняється від юридичної)"
                rows={2}
                className="w-full px-4 py-3 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Контактний телефон організації"
                required
                hint={phoneError ? undefined : (idGovFields.phone ? "Попередньо заповнено з id.gov.ua, можна змінити" : "Формат: +380XXXXXXXXX")}
                error={phoneError || undefined}
              >
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onBlur={(e) => handlePhoneBlur(e.target.value)}
                  placeholder="+380"
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] bg-white text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                    phoneError
                      ? "border-destructive/50 focus:border-destructive"
                      : "border-border/50 focus:border-primary/50"
                  )}
                />
              </Field>

              <Field
                label="Електронна адреса організації"
                required
                hint={emailError ? undefined : (idGovFields.email ? "Попередньо заповнено з id.gov.ua, можна змінити" : "Формат: user@example.com")}
                error={emailError || undefined}
              >
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={(e) => handleEmailBlur(e.target.value)}
                  placeholder="email@example.com"
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors",
                    emailError
                      ? "border-destructive/50 focus:border-destructive"
                      : "border-border/50 focus:border-primary/50"
                  )}
                />
              </Field>
            </div>

            <Field
              label="IBAN рахунок"
              required
              error={ibanError ? (ibanBankName ? `${ibanError} (${ibanBankName})` : ibanError) : undefined}
              hint={
                ibanError 
                  ? undefined 
                  : isValidatingBank 
                    ? "Перевірка банку..." 
                    : ibanBankName 
                      ? `✓ ${ibanBankName}` 
                      : "Рахунок із спеціальним режимом використання"
              }
            >
              <input
                type="text"
                value={profile.iban}
                onChange={(e) => handleIbanChange(e.target.value)}
                onBlur={(e) => handleIbanBlur(e.target.value)}
                placeholder="UA000000000000000000000000000"
                className={cn(
                  "w-full h-11 px-4 rounded-xl border-[3px] bg-white text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                  ibanError 
                    ? "border-destructive/50 focus:border-destructive" 
                    : ibanBankName 
                      ? "border-green-300 focus:border-green-400" 
                      : "border-border/50 focus:border-primary/50"
                )}
              />
            </Field>
          </div>

          {/* Save button */}
          <div className="mt-6 pt-5 border-t border-border/50 flex items-center justify-between">
            <div>
              {isLoading ? (
                <p className="text-[12px] text-muted-foreground">
                  Завантаження профілю...
                </p>
              ) : saveError ? (
                <p className="text-[12px] text-destructive">
                  {saveError}
                </p>
              ) : !isProfileComplete ? (
                <p className="text-[12px] text-muted-foreground">
                  Заповніть обов'язкові поля для збереження
                </p>
              ) : null}
            </div>
            <Button
              onClick={handleSave}
              disabled={!isProfileComplete || isSaving || isLoading || !!ibanError || !!edrpouError || !!emailError || !!phoneError}
              className={cn(
                "gap-2 min-w-[160px] transition-all",
                saved && "bg-green-600 hover:bg-green-600"
              )}
            >
              {isSaving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Збереження...
                </>
              ) : saved ? (
                <>
                  <SaveIcon className="w-4 h-4" />
                  Збережено!
                </>
              ) : (
                "Зберегти профіль"
              )}
            </Button>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-[3px] border-blue-100 p-5">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <LockIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-[13px] font-medium text-blue-900 mb-1">
                Дані з id.gov.ua захищені
              </p>
              <p className="text-[12px] text-blue-700 leading-relaxed">
                Поля позначені{" "}
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-200/50 text-blue-800 rounded text-[10px] font-medium">
                  <LockIcon className="w-2.5 h-2.5" />
                  id.gov.ua
                </span>{" "}
                отримані з державної системи ідентифікації та не можуть бути змінені.
                Контактні дані організації можуть відрізнятися від особистих.
              </p>
            </div>
          </div>
        </div>

      </div>
    </AppLayout>
  );
}

export default Settings;
